// Formatação dos valores do Desempenho, compartilhada entre o painel e a análise individual
// para o mesmo indicador aparecer escrito do mesmo jeito nas duas telas.

const currencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

// Eixos e rótulos curtos dos gráficos: "R$ 13 mil" em vez de "R$ 13.000,00".
const compactCurrencyFormatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    maximumFractionDigits: 1
});

const decimalFormatter = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

export const formatCompactCurrency = (value) => compactCurrencyFormatter.format(Number(value || 0));

// Vírgula decimal, como no restante da interface em pt-BR ("39,4%").
export const formatPercent = (value) => `${decimalFormatter.format(Number(value || 0))}%`;

export const formatInteger = (value) => Math.round(Number(value || 0)).toLocaleString("pt-BR");

// Tipos de indicador medidos em reais; taxas em percentual; os demais são quantidades.
export const CURRENCY_INDICATOR_TYPES = ["REVENUE", "PRODUCT_TICKET", "CATEGORY_REVENUE", "CATEGORY_TICKET"];
export const PERCENT_INDICATOR_TYPES = ["RETURN_RATE", "OCCUPANCY_RATE"];

export const formatIndicatorValue = (indicator, value) => {
    const type = indicator?.type;
    if (CURRENCY_INDICATOR_TYPES.includes(type)) {
        return formatCurrency(value);
    }
    if (PERCENT_INDICATOR_TYPES.includes(type)) {
        return formatPercent(value);
    }
    return formatInteger(value);
};

/**
 * Por que um indicador está sem valor e não conta para o nível (insufficientBase do backend).
 * Na taxa de retorno faltam clientes avulsos; na taxa de ocupação falta expediente para medir:
 * filial sem horário cadastrado, colaborador fora da filial do filtro ou período que ainda não
 * começou. "Base insuficiente" na ocupação faria o gestor procurar clientes que não são o problema.
 */
export const describeNeutralIndicator = (indicator) => (
    indicator?.type === "OCCUPANCY_RATE" ? "Sem expediente para medir" : "Base insuficiente"
);

// "2026-08-02" vira "02/08". A data chega do backend sem fuso (LocalDate), então é recortada como
// texto: passar por Date a leria em UTC e poderia mostrar o dia anterior.
const formatDayMonth = (isoDate) => {
    const [, month, day] = String(isoDate || "").split("-");
    return day && month ? `${day}/${month}` : "";
};

/**
 * Explica a taxa de retorno em uma frase, com a conta que o backend fez (ReturnRateService):
 * quantos clientes avulsos entraram na base, de quando, e quantos voltaram dentro do prazo. Prazo e
 * base mínima vêm na resposta, para a tela não repetir a regra.
 *
 * @param {{customers:number, returned:number, cohortStart:string, cohortEnd:string,
 *          insufficientBase:boolean, windowDays:number, minBase:number}=} detail
 */
export const describeReturnRate = (detail) => {
    if (!detail) {
        return "";
    }
    const customers = Number(detail.customers || 0);
    const clients = customers === 1 ? "cliente avulso" : "clientes avulsos";
    if (detail.insufficientBase) {
        return `Base insuficiente: ${formatInteger(customers)} ${clients} (mínimo ${formatInteger(detail.minBase)}). Não conta para o nível.`;
    }
    return `${formatInteger(detail.returned)} de ${formatInteger(customers)} ${clients} atendidos de `
        + `${formatDayMonth(detail.cohortStart)} a ${formatDayMonth(detail.cohortEnd)} voltaram em até `
        + `${formatInteger(detail.windowDays)} dias.`;
};

/**
 * Separa os grupos do ticket por categoria (CategoryTicketService) em tipos (serviços e produtos)
 * e categorias do cadastro. O painel e a análise mostram as duas partes em blocos separados porque
 * uma categoria pode ter nome parecido com o tipo ("Serviço" e "Serviços") e medir outra coisa.
 *
 * @param {Array<{key: string}>=} groups grupos na ordem do backend
 * @returns {{types: Array, categories: Array}}
 */
export const splitTicketGroups = (groups) => {
    const list = groups || [];
    return {
        types: list.filter((group) => !String(group.key).startsWith("CATEGORY_")),
        categories: list.filter((group) => String(group.key).startsWith("CATEGORY_"))
    };
};
